import {
  APP_VERSION,
  EVENTS,
} from '@/lib/consts.js'

import {
  getCategories,
  getDeviceType,
  getExternalId,
  getOwnerId,
  getBusinessPrefixedPhone,
  getDateText,
  getMaximumTravelDistance,
  getRegistrationMethod,
  simplifyCategory,
  setOpenedBusinessData,
  groupInternalNames,
  getBusinessMarketingScore,
} from '@/lib/analytics/helpers.js'
import { ONBOARDING_USER_ROLE, PRICE_TYPE, DEFAULT_CURRENCY } from '@/lib/analytics/consts.js'
import { path } from 'ramda'
import { useOnboardingPolls } from '@/components/onboarding/composables/polls/index.js'
import { REGISTRATION_POLLS } from '@/components/onboarding/composables/polls/consts.js'

export const gtm1Onboarding = {
  name: 'gtm1Onboarding',

  [EVENTS.ONBOARDING_REFERRAL_VIEW_OPENED] ({ data, country, device }) {
    const business_name = path(['business', 'name'], data)
    const device_type = getDeviceType(device)

    return {
      event: 'Pre_Onboarding_Referral_Invited',
      properties: {
        app_version: APP_VERSION,
        business_name,
        country,
        device,
        device_type,
        user_role: ONBOARDING_USER_ROLE,
      },
    }
  },

  [EVENTS.ONBOARDING_NEW_EMAIL_ENTERED] ({ data, country, device, user }) {
    const loginType = path(['loginType'], data)
    const registration_method = getRegistrationMethod(loginType)
    const device_type = getDeviceType(device)
    const email = path(['email'], user)
    const onboarding_start_date = getDateText(new Date())

    return {
      event: 'Business_Registration_Started_With_Email',
      properties: {
        app_version: APP_VERSION,
        country,
        device,
        device_type,
        email,
        onboarding_start_date,
        registration_method,
        user_role: ONBOARDING_USER_ROLE,
      },
    }
  },

  [EVENTS.ONBOARDING_COUNTRY_SELECTION] ({
    appType,
    user,
    business,
    country,
    device,
    data: {
      country_selected,
    },
  }) {
    const device_type = getDeviceType(device)
    const email = path(['email'], user)
    const business_id = path(['email'], business)
    const business_phone = getBusinessPrefixedPhone({ business, country })

    return {
      event: 'Onboarding_Country_Selection',
      properties: {
        app_version: APP_VERSION,
        country,
        country_selected,
        device,
        device_type,
        email,
        business_id,
        business_phone,
      },
    }
  },

  [EVENTS.ONBOARDING_AGREEMENTS_UPDATED] ({
    data,
    user,
    country,
    device,
  }) {
    const device_type = getDeviceType(device)
    const email = path(['email'], user)
    const items = path(['items'], data) || []
    const marketing_agreement = items.find(item => item.name === 'marketing_agreement')
    const business_marketing_agreement = path(['value'], marketing_agreement)

    return {
      event: 'Business_Contact_Preferences_Updated',
      properties: {
        app_version: APP_VERSION,
        business_marketing_agreement,
        country,
        device,
        device_type,
        email,
        user_role: ONBOARDING_USER_ROLE,
      },
    }
  },

  [EVENTS.ONBOARDING_BUSINESS_NAME_SET] ({ data, user, country, device, routingConfig }) {
    const business = path(['business'], data) || {}
    const business_name = path(['name'], business)
    const device_type = getDeviceType(device)
    const email = path(['email'], user)
    const business_id = getExternalId({ id: business.id, country, routingConfig })

    return {
      event: 'Onboarding_Business_Name_Set',
      properties: {
        app_version: APP_VERSION,
        business_id,
        business_name,
        country,
        device,
        device_type,
        email,
        user_role: ONBOARDING_USER_ROLE,
      },
    }
  },

  [EVENTS.ONBOARDING_OWNER_NAME_SET] ({ data, appType, user, country, device, routingConfig }) {
    const business = path(['business'], data) || {}
    const owner_name = path(['owner_full_name'], business)
    const device_type = getDeviceType(device)
    const email = path(['email'], user)
    const business_id = getExternalId({ id: business.id, country, routingConfig })

    return {
      event: 'Onboarding_Owner_Name_Set',
      properties: {
        app_version: APP_VERSION,
        business_id,
        country,
        device,
        device_type,
        email,
        owner_name,
        user_role: ONBOARDING_USER_ROLE,
      },
    }
  },

  [EVENTS.ONBOARDING_BUSINESS_PHONE_SET] ({ data, appType, user, country, device, routingConfig }) {
    const business = path(['business'], data) || {}
    const business_phone = getBusinessPrefixedPhone({ business, country })
    const device_type = getDeviceType(device)
    const email = path(['email'], user)
    const business_id = getExternalId({ id: business.id, country, routingConfig })

    return {
      event: 'Onboarding_Business_Phone_Set',
      properties: {
        app_version: APP_VERSION,
        business_id,
        business_phone,
        country,
        device,
        device_type,
        email,
        user_role: ONBOARDING_USER_ROLE,
      },
    }
  },

  [EVENTS.ONBOARDING_ABOUT_YOU_FINISHED] ({ data, appType, user, country, device, routingConfig }) {
    const business = path(['business'], data) || {}
    const business_name = path(['name'], business)
    const business_phone = getBusinessPrefixedPhone({ business, country })
    const device_type = getDeviceType(device)
    const email = path(['email'], user)
    const business_id = getExternalId({ id: business.id, country, routingConfig })
    const owner_name = path(['owner_full_name'], business)
    const stripe_acknowledgement = data?.stripeAcknowledgement

    return {
      event: 'Onboarding_About_You',
      properties: {
        app_version: APP_VERSION,
        business_id,
        business_name,
        business_phone,
        country,
        device,
        device_type,
        email,
        owner_name,
        stripe_acknowledgement,
        user_role: ONBOARDING_USER_ROLE,
      },
    }
  },

  [EVENTS.ONBOARDING_PASSWORD_SET] ({ appType, business, user, country, device, routingConfig }) {
    business = business || {}

    const device_type = getDeviceType(device)
    const email = path(['email'], user)

    return {
      event: 'Onboarding_Password_Set',
      properties: {
        app_version: APP_VERSION,
        country,
        device,
        device_type,
        email,
        user_role: ONBOARDING_USER_ROLE,
      },
    }
  },

  [EVENTS.ONBOARDING_B_LISTING_CLAIMED] ({ appType, business, user, country, device, routingConfig }) {
    business = business || {}

    const device_type = getDeviceType(device)
    const email = path(['email'], user)
    const business_id = getExternalId({ id: business.id, country, routingConfig })

    return {
      event: 'Onboarding_Blisting_Claim',
      properties: {
        app_version: APP_VERSION,
        business_id,
        country,
        device,
        device_type,
        email,
        user_role: ONBOARDING_USER_ROLE,
      },
    }
  },

  [EVENTS.ONBOARDING_REFERRAL_CODE] ({ data }) {
    const { referral_code, referral_code_status } = data

    return {
      event: 'Onboarding_Referral_Code',
      properties: {
        referral_code,
        referral_code_status,
      },
    }
  },

  [EVENTS.ONBOARDING_BUSINESS_CREATED] ({ data, appType, country, device, routingConfig }) {
    const business = path(['business'], data) || {}
    const device_type = getDeviceType(device)
    const business_id = getExternalId({ id: business.id, country, routingConfig })
    const business_name = path(['name'], business)
    const business_phone = getBusinessPrefixedPhone({ business, country })

    return {
      event: 'Business_Registration_Started_Backend',
      properties: {
        app_version: APP_VERSION,
        business_id,
        business_name,
        business_phone,
        country,
        device,
        device_type,
        email: business.owner_email,
        user_role: ONBOARDING_USER_ROLE,
      },
    }
  },

  [EVENTS.ONBOARDING_BUSINESS_REGISTRATION_CATEGORY_SELECTED] ({
    appType,
    business,
    device,
    country,
    categories,
    primaryCategory,
    routingConfig,
  }) {
    business = business || {}

    const business_id = getExternalId({ id: business.id, country, routingConfig })
    const business_primary_category = simplifyCategory(primaryCategory)
    const simplifiedCategories = getCategories(categories)
    const device_type = getDeviceType(device)

    return {
      event: 'Business_Registration_Category_Selected',
      properties: {
        app_version: APP_VERSION,
        business_id,
        business_name: business.name,
        business_primary_category,
        categories: simplifiedCategories,
        country,
        device,
        device_type,
        email: business.owner_email,
        user_role: ONBOARDING_USER_ROLE,
      },
    }
  },

  [EVENTS.ONBOARDING_ADDRESS] ({ data, appType, country, device, routingConfig }) {
    const business = path(['business'], data) || {}
    const business_id = getExternalId({ id: business.id, country, routingConfig })
    const location = path(['location'], business) || {}
    const device_type = getDeviceType(device)
    const rentingVenue = path(['renting_venue'], business)
    const is_shared_location = !!rentingVenue
    const name_of_shared_location = path(['name'], rentingVenue)
    const business_phone = getBusinessPrefixedPhone({ business, country })
    const { screen_version, is_address_adjusted } = data

    return {
      event: 'Onboarding_Address',
      properties: {
        app_version: APP_VERSION,
        business_id,
        business_name: business.name,
        business_phone,
        city: location.city,
        country,
        device,
        device_type,
        email: business.owner_email,
        is_shared_location,
        name_of_shared_location,
        owner_email: business.owner_email,
        owner_name: business.owner_full_name,
        // postalCode is named with camel case convencion, because Segment recognizes this as special value
        postalCode: location.zipcode,
        shared_location_room_number: location.address3,
        street_address_and_number: location.address,
        user_role: ONBOARDING_USER_ROLE,
        screen_version,
        is_address_adjusted,
      },
    }
  },

  [EVENTS.ONBOARDING_WORKPLACE] ({ data, appType, business, user, country, device, routingConfig }) {
    const device_type = getDeviceType(device)
    const email = user?.email
    const business_id = getExternalId({ id: business?.id, country, routingConfig })
    const business_name = business?.name
    const business_phone = getBusinessPrefixedPhone({ business, country })
    const traveling_to_client = data?.type

    return {
      event: 'Onboarding_Workplace',
      properties: {
        app_version: APP_VERSION,
        business_id,
        business_name,
        business_phone,
        country,
        device,
        device_type,
        email,
        traveling_to_client,
        user_role: ONBOARDING_USER_ROLE,
      },
    }
  },

  [EVENTS.ONBOARDING_ADDRESS_SEARCH] ({ data, appType, country, device, routingConfig }) {
    const business = path(['business'], data) || {}
    const business_id = getExternalId({ id: business.id, country, routingConfig })
    const location = path(['location'], business) || {}
    const device_type = getDeviceType(device)
    const business_phone = getBusinessPrefixedPhone({ business, country })

    return {
      event: 'Onboarding_Address_Search',
      properties: {
        app_version: APP_VERSION,
        business_id,
        business_name: business.name,
        business_phone,
        city: location.city,
        country,
        device,
        device_type,
        email: business.owner_email,
        owner_email: business.owner_email,
        owner_name: business.owner_full_name,
        // postalCode is named with camel case convencion, because Segment recognizes this as special value
        postalCode: location.zipcode,
        street_address_and_number: location.address,
        user_role: ONBOARDING_USER_ROLE,
      },
    }
  },

  [EVENTS.ONBOARDING_MAP_LOCATION] ({ data, appType, country, device, routingConfig }) {
    const business = path(['business'], data) || {}
    const business_id = getExternalId({ id: business.id, country, routingConfig })
    const location = path(['location'], business) || {}
    const device_type = getDeviceType(device)
    const latitude = path(['coordinate', 'latitude'], location)
    const longitude = path(['coordinate', 'longitude'], location)
    const business_phone = getBusinessPrefixedPhone({ business, country })
    const { entry_source } = data

    return {
      event: 'Onboarding_Map_Location',
      properties: {
        app_version: APP_VERSION,
        business_id,
        business_name: business.name,
        business_phone,
        city: location.city,
        country,
        device,
        device_type,
        email: business.owner_email,
        latitude,
        longitude,
        owner_email: business.owner_email,
        owner_name: business.owner_full_name,
        user_role: ONBOARDING_USER_ROLE,
        entry_source,
      },
    }
  },

  [EVENTS.ONBOARDING_EDIT_LOCATION_PIN] ({ data, appType, country, device, routingConfig }) {
    const business = path(['business'], data) || {}
    const business_id = getExternalId({ id: business.id, country, routingConfig })
    const device_type = getDeviceType(device)
    const { location_default, location_map_moves, location_adjusted, is_location_adjusted } = data
    const business_phone = getBusinessPrefixedPhone({ business, country })

    return {
      event: 'Onboarding_Edit_Location_Pin',
      properties: {
        app_version: APP_VERSION,
        business_id,
        business_name: business.name,
        business_phone,
        country,
        device,
        device_type,
        email: business.owner_email,
        user_role: ONBOARDING_USER_ROLE,
        location_default,
        location_map_moves,
        location_adjusted,
        is_location_adjusted,
      },
    }
  },

  [EVENTS.ONBOARDING_ENTER_YOUR_ADDRESS] ({ data, appType, country, device, routingConfig }) {
    const business = path(['business'], data) || {}
    const business_id = getExternalId({ id: business.id, country, routingConfig })
    const device_type = getDeviceType(device)
    const userId = getOwnerId({ business, country, routingConfig })
    const business_phone = getBusinessPrefixedPhone({ business, country })

    return {
      event: 'Onboarding_Enter_Your_Address',
      userId,
      properties: {
        app_version: APP_VERSION,
        business_id,
        business_phone,
        country,
        device_type,
        email: business.owner_email,
        user_role: ONBOARDING_USER_ROLE,
      },
    }
  },

  [EVENTS.ONBOARDING_TRAVEL_FEE] ({ data, appType, country, device, routingConfig }) {
    const business = path(['business'], data) || {}
    const device_type = getDeviceType(device)
    const business_id = getExternalId({ id: business.id, country, routingConfig })
    const traveling = business.traveling || {}
    const maximum_travel_distance = getMaximumTravelDistance(traveling)
    const travel_fee = parseFloat(traveling.price) || 0
    const business_phone = getBusinessPrefixedPhone({ business, country })

    return {
      event: 'Onboarding_Travel_Fee',
      properties: {
        app_version: APP_VERSION,
        business_id,
        business_name: business.name,
        business_phone,
        country,
        device,
        device_type,
        email: business.owner_email,
        maximum_travel_distance,
        owner_email: business.owner_email,
        owner_name: business.owner_full_name,
        price_type: PRICE_TYPE[traveling.price_type],
        travel_fee,
        user_role: ONBOARDING_USER_ROLE,
      },
    }
  },

  [EVENTS.ONBOARDING_BUSINESS_HOURS] ({ data, appType, country, device, routingConfig }) {
    const business = path(['business'], data) || {}
    const device_type = getDeviceType(device)
    const business_id = getExternalId({ id: business.id, country, routingConfig })
    const business_phone = getBusinessPrefixedPhone({ business, country })

    return {
      event: 'Onboarding_Business_Hours',
      properties: {
        app_version: APP_VERSION,
        business_id,
        business_name: business.name,
        business_phone,
        country,
        device,
        device_type,
        email: business.owner_email,
        owner_email: business.owner_email,
        owner_name: business.owner_full_name,
        user_role: ONBOARDING_USER_ROLE,
      },
    }
  },

  [EVENTS.ONBOARDING_SERVICE] ({ data, appType, country, device, routingConfig }) {
    const business = path(['business'], data) || {}
    const device_type = getDeviceType(device)
    const business_id = getExternalId({ id: business.id, country, routingConfig })
    const business_phone = getBusinessPrefixedPhone({ business, country })

    return {
      event: 'Onboarding_Service',
      properties: {
        app_version: APP_VERSION,
        business_id,
        business_name: business.name,
        business_phone,
        country,
        device,
        device_type,
        email: business.owner_email,
        owner_email: business.owner_email,
        owner_name: business.owner_full_name,
        user_role: ONBOARDING_USER_ROLE,
      },
    }
  },

  [EVENTS.ONBOARDING_BUSINESS_REGISTRATION_COMPLETED] ({
    appType,
    business,
    device,
    country,
    categories,
    primaryCategory,
    routingConfig,
  }) {
    business = business || {}

    const { getPoll } = useOnboardingPolls()
    const location = path(['location'], business) || {}
    const business_id = getExternalId({ id: business.id, country, routingConfig })
    const business_primary_category = simplifyCategory(primaryCategory)
    const simplifiedCategories = getCategories(categories)
    const device_type = getDeviceType(device)
    const number_of_staffers = getPoll(REGISTRATION_POLLS.NUMBER_OF_STAFFERS)?.savedInternalNamesChoices || null
    const other_app = getPoll(REGISTRATION_POLLS.OTHER_APPS)?.savedInternalNamesChoices || null

    return {
      event: 'Business_Registration_Completed',
      properties: {
        app_version: APP_VERSION,
        business_id,
        business_name: business.name,
        business_primary_category,
        categories: simplifiedCategories,
        city: location.city,
        country,
        device,
        device_type,
        email: business.owner_email,
        // postalCode is named with camel case convencion, because Segment recognizes this as special value
        postalCode: location.zipcode,
        user_role: ONBOARDING_USER_ROLE,
        number_of_staffers,
        other_app,
        currency: DEFAULT_CURRENCY,
        value: getBusinessMarketingScore(business_primary_category),
      },
    }
  },

  [EVENTS.ONBOARDING_DELAY_SET] ({
    business,
    data,
  }) {
    business = business || {}

    const business_name = path(['name'], business)

    return {
      event: 'Onboarding_Delay_Set',
      properties: {
        business_name,
        delay_set: data.delay_set,
      },
    }
  },

  [EVENTS.ONBOARDING_RESOLVED] ({ business, country, device, routingConfig }) {
    if (this.cannotSendAppOpened({ business })) {
      return this.sendingSkipped
    }

    setOpenedBusinessData({ business })
    return this.getAppOpenedPayload({ business, country, device, routingConfig })
  },

  [EVENTS.ONBOARDING_SERVICES_CONCIERGE] () {
    return {
      event: 'Onboarding_Services_Concierge',
    }
  },

  [EVENTS.ONBOARDING_SERVICES_ADD_MORE] () {
    return {
      event: 'Onboarding_Services_Add_More',
    }
  },

  [EVENTS.BUSINESS_ONBOARDING_ACTION] ({ data }) {
    const internalNamesPayload = groupInternalNames(data.internalNames, 5)

    const properties = {
      screen_name: data.screenName,
      event_action: data.eventAction,
      zipcode: data.zipcode,
      city: data.city,
      address: data.address,
      address2: data.address2,
      latitude: data.latitude,
      longitude: data.longitude,
      no_of_services: data.noOfServices,
      no_of_services_missing_info: data.noOfServicesMissingInfo,
      message_error: data.messageError,
      channel_package_name: data.channelPackageName,
      page_source: data.pageSource,
      service_id: data.serviceId,
      suggested_services_left: data.suggestedServicesLeft,
      internal_name: data.internalName,
      ...internalNamesPayload,
      is_shared_location: data.isSharedLocation,
      is_address_adjusted: data.isAddressAdjusted,
      street_adjusted: data.streetAdjusted,
      apartment_adjusted: data.apartmentAdjusted,
      city_adjusted: data.cityAdjusted,
      zipcode_adjusted: data.zipcodeAdjusted,
      entry_source: data.entrySource,
      is_extole_referral: data.isExtoleReferral,
      advocate_code: data.advocateCode,
      number_of_hints: data.numberOfHints,
      hint_type: data.hintType,
      empty_street_number: data.emptyStreetNumber,
      added_more_details: data.addedMoreDetails,
      agreement_type: data.agreementType,
    }

    if (data.suite_number) {
      properties.suite_number = data.suite_number
    }

    if (data.message) {
      properties.message = data.message
    }

    return {
      event: 'Business_Onboarding_Action',
      properties,
    }
  },
}
